"use client"

import { useRef } from "react"
import { gsap } from "gsap"
import { useGSAP } from "@gsap/react"
import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin"
import { SplitText } from "gsap/SplitText"

export const useScrambleAnimation = (
    options: { lowercase?: boolean; uppercase?: boolean; speed?: number } = {},
) => {
    const { lowercase = false, uppercase = false, speed = 0.6 } = options
    const textRef = useRef<HTMLElement | null>(null)
    const splitTextRef = useRef<SplitText | null>(null)
    const { contextSafe } = useGSAP({ scope: textRef })

    useGSAP(() => {
        gsap.registerPlugin(SplitText, ScrambleTextPlugin)
    }, [])

    const play = contextSafe(() => {
        if (!textRef.current) return

        const originalText =
            textRef.current.dataset.text || textRef.current.textContent || ""
        textRef.current.textContent = originalText

        splitTextRef.current?.revert()

        const split = new SplitText(textRef.current, {
            type: "chars",
            charsClass: uppercase ? "" : "lowercase",
        })
        splitTextRef.current = split

        const targetText = originalText.split("").filter((char) => char !== " ")

        const timeline = gsap.timeline()
        const staggerDelay = 0.03
        const duration = split.chars.length * staggerDelay

        timeline.to(
            split.chars,
            {
                opacity: 0.2,
                duration: duration,
                stagger: staggerDelay,
                ease: "power2.out",
            },
            0,
        )

        timeline.to(
            split.chars,
            {
                opacity: 1,
                duration: duration,
                stagger: 0.04,
                ease: "power2.out",
            },
            duration,
        )

        split.chars.forEach((char, index) => {
            if (targetText[index]) {
                timeline.to(
                    char,
                    {
                        scrambleText: {
                            text: targetText[index],
                            speed: speed,
                            chars: getScrambleChars({ lowercase, uppercase }),
                        },
                        duration: 0.7,
                        ease: "none",
                    },
                    index * staggerDelay + 0.05,
                )
            }
        })
    })

    return { textRef, play }
}

const getScrambleChars = ({
    lowercase = false,
    uppercase = true,
}: {
    lowercase?: boolean
    uppercase?: boolean
}) => {
    const lower = "abcdefghijklmnopqrstuvwxyz"
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
    if (lowercase) return lower
    if (uppercase) return upper
    return lower + upper
}
